(function () {
  'use strict';

  const RATE_KEYS = ['base', 'sizeStep', 'extraStep', 'streamers'];
  const money = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });
  const number = new Intl.NumberFormat('en-US');
  const swedishMoney = new Intl.NumberFormat('sv-SE', { style: 'currency', currency: 'SEK' });
  const swedishNumber = new Intl.NumberFormat('sv-SE');

  function integer(value) {
    if (value === null || value === undefined || String(value).trim() === '') return null;
    const parsed = Number(value);
    return Number.isSafeInteger(parsed) ? parsed : null;
  }

  function formatWeight(grams, market = 'US') {
    if (!Number.isFinite(grams) || grams < 0) return '—';
    if (market === 'SE') return grams + ' g';
    // Display ounces rounded down; calculations and specifications retain exact grams.
    const ounces = Math.floor((grams / 28.349523125) * 10) / 10;
    return ounces.toFixed(1) + ' oz (' + grams + ' g)';
  }

  function formatBottleSize(centimeters, market = 'US') {
    if (!Number.isFinite(centimeters) || centimeters <= 0) return '—';
    if (market === 'SE') return centimeters + ' cm';
    return (centimeters / 2.54).toFixed(1) + ' in (' + centimeters + ' cm)';
  }

  // Liquid supplies this configuration from aaa-cannon-config; no price fallback
  // is kept in JavaScript. Invalid or missing data must never produce an estimate.
  function createEstimator(config) {
    const positive = (value) => Number.isSafeInteger(value) && value > 0;
    const nonnegative = (value) => Number.isSafeInteger(value) && value >= 0;
    const invalid = () => { throw new Error('Invalid cannon estimate configuration'); };
    if (!config || config.version !== 1 || !['US', 'SE'].includes(config.market)
      || !Array.isArray(config.sizes) || config.sizes.length === 0
      || !positive(config.minimumQuantity) || !positive(config.maximumQuantity)
      || config.maximumQuantity < config.minimumQuantity || !positive(config.extraStepGrams)
      || !Array.isArray(config.extraAmounts) || config.extraAmounts[0] !== 0
      || !config.extraAmounts.every((amount) => nonnegative(amount) && amount % config.extraStepGrams === 0)
      || !Array.isArray(config.quantityPresets) || config.quantityPresets.length === 0
      || !config.quantityPresets.every((amount) => positive(amount) && amount >= config.minimumQuantity && amount <= config.maximumQuantity)
      || !config.rates || !RATE_KEYS.every((key) => nonnegative(config.rates[key])) || !positive(config.rates.base)) invalid();
    const SIZES = {};
    const SIZE_CM = {};
    const METRIC_SIZE_KEYS = {};
    for (const size of config.sizes) {
      if (!size || typeof size.key !== 'string' || !/^\d+$/.test(size.key) || Object.hasOwn(SIZES, size.key)
        || !positive(size.cm) || Object.hasOwn(METRIC_SIZE_KEYS, size.cm) || !nonnegative(size.step)
        || !positive(size.bottle) || !positive(size.confetti) || !positive(size.carton)) invalid();
      SIZES[size.key] = Object.freeze({ step: size.step, bottle: size.bottle, confetti: size.confetti });
      SIZE_CM[size.key] = size.cm;
      METRIC_SIZE_KEYS[size.cm] = size.key;
    }
    if ((config.market === 'SE' && !config.rates.bySize)
      || (config.rates.bySize && !Object.keys(SIZES).every((key) => positive(config.rates.bySize[key])))) invalid();
    Object.freeze(SIZES);
    Object.freeze(SIZE_CM);
    Object.freeze(METRIC_SIZE_KEYS);
    const rates = Object.freeze({ ...config.rates, ...(config.rates.bySize ? { bySize: Object.freeze({ ...config.rates.bySize }) } : {}) });
    const EXTRA_AMOUNTS = Object.freeze([...config.extraAmounts]);
    const QUANTITY_PRESETS = Object.freeze([...config.quantityPresets]);
    const MINIMUM_QUANTITY = config.minimumQuantity;
    const MAXIMUM_QUANTITY = config.maximumQuantity;
    const EXTRA_STEP_GRAMS = config.extraStepGrams;
    const sizeKey = (value, market = config.market) => market === 'SE' ? METRIC_SIZE_KEYS[String(value)] || String(value) : String(value);

    // All arithmetic uses integer cents. The result is an estimate, never a checkout price.
    function calculateEstimate(input, estimateRates = rates, market = config.market) {
      const rates = estimateRates;
      const errors = {};
      const canonicalSize = sizeKey(input.size, market);
      const size = SIZES[canonicalSize];
      const effect = input.effect;
      const quantity = integer(input.quantity);
      const extra = integer(input.extraFilling);
      if (!size) errors.size = market === 'SE' ? 'Välj rörets storlek.' : 'Please choose a cannon size.';
      if (effect !== 'confetti' && effect !== 'streamers') errors.effect = market === 'SE' ? 'Välj konfetti eller streamers.' : 'Please choose confetti or streamers.';
      if (quantity === null || quantity < MINIMUM_QUANTITY || quantity > MAXIMUM_QUANTITY) {
        errors.quantity = market === 'SE' ? 'Ange ett heltal mellan ' + MINIMUM_QUANTITY + ' och ' + swedishNumber.format(MAXIMUM_QUANTITY).replace(/\u00a0/g, ' ') + ' rör.' : 'Enter a whole number between ' + MINIMUM_QUANTITY + ' and ' + number.format(MAXIMUM_QUANTITY) + ' cannons.';
      }
      if (!EXTRA_AMOUNTS.includes(extra)) errors.extraFilling = market === 'SE' ? 'Välj mängden extra fyllning.' : 'Please choose an extra filling amount.';
      const validSizeRates = !rates.bySize || Object.keys(SIZES).every((key) => Number.isSafeInteger(rates.bySize[key]) && rates.bySize[key] > 0);
      if (!RATE_KEYS.every((key) => Number.isSafeInteger(rates[key]) && rates[key] >= 0) || rates.base === 0 || !validSizeRates || (market === 'SE' && !rates.bySize)) {
        errors.rates = market === 'SE' ? 'Priset kan inte beräknas. Kontakta oss för en offert.' : 'An estimate is unavailable. Please request a quote.';
      }
      if (Object.keys(errors).length) return { valid: false, errors };

      const baseCents = rates.base;
      const sizeCents = rates.bySize ? rates.bySize[canonicalSize] - baseCents : size.step * rates.sizeStep;
      const cannonCents = baseCents + sizeCents;
      const effectCents = effect === 'streamers' ? rates.streamers : 0;
      const extraCents = (extra / EXTRA_STEP_GRAMS) * rates.extraStep;
      const unitCents = cannonCents + effectCents + extraCents;
      const totalCents = unitCents * quantity;
      if (!Number.isSafeInteger(totalCents)) {
        return { valid: false, errors: { rates: market === 'SE' ? 'Priset kan inte beräknas. Kontakta oss för en offert.' : 'An estimate is unavailable. Please request a quote.' } };
      }
      return {
        valid: true,
        size: String(input.size), effect, quantity,
        ...(market === 'SE' ? { market: 'SE', currency: 'SEK', sizeCm: SIZE_CM[canonicalSize] } : {}),
        baseCents, sizeCents, cannonCents, effectCents, extraCents, unitCents, totalCents,
        bottleCm: size.bottle,
        includedFillingGrams: size.confetti,
        totalFillingGrams: size.confetti + extra,
        includedConfettiGrams: effect === 'confetti' ? size.confetti : 0,
        extraFillingGrams: extra,
        extraConfettiGrams: effect === 'confetti' ? extra : 0,
        totalConfettiGrams: effect === 'confetti' ? size.confetti + extra : 0
      };
    }

    function quoteSummary(estimate) {
      if (!estimate.valid) return '';
      if (estimate.market === 'SE') {
        const fillingName = estimate.effect === 'streamers' ? 'streamers' : 'konfetti';
        return [
          'PRISUPPSKATTNING — SPECIALDESIGNADE KONFETTIRÖR — SEK',
          'Marknad: Sverige (SE); valuta: SEK; samtliga priser exklusive moms.',
          'Storlek: ' + estimate.sizeCm + ' cm; tryckbehållare: ' + formatBottleSize(estimate.bottleCm, 'SE'),
          'Fyllning: ' + formatWeight(estimate.totalFillingGrams, 'SE') + ' ' + fillingName + '; ingår: ' + formatWeight(estimate.includedFillingGrams, 'SE') + '; extra: ' + formatWeight(estimate.extraFillingGrams, 'SE'),
          'Färger och material: Papper, metallic och biologiskt nedbrytbart. Valen bekräftas efter förfrågan.',
          'Antal: ' + swedishNumber.format(estimate.quantity) + ' rör; kartongpackning bekräftas med offerten.',
          'Per rör: ' + estimate.sizeCm + ' cm rör ' + swedishMoney.format(estimate.cannonCents / 100) + ' + val av fyllning ' + swedishMoney.format(estimate.effectCents / 100) + ' + extra ' + fillingName + ' ' + swedishMoney.format(estimate.extraCents / 100),
          'Beräknat styckepris: ' + swedishMoney.format(estimate.unitCents / 100),
          'Beräknat produktbelopp: ' + swedishMoney.format(estimate.totalCents / 100),
          'Tryck av kundens färdiga design ingår. Ingen ytterligare rabatt beräknas.',
          'Minsta antal: ' + MINIMUM_QUANTITY + ' rör.',
          'Detta är en prisuppskattning. Vi följer upp designfiler, specifikationer, packning, leverans och slutpris före produktion.'
        ].join('\n');
      }
      const filling = formatWeight(estimate.totalFillingGrams) + ' ' + estimate.effect
        + '; included: ' + formatWeight(estimate.includedFillingGrams) + '; extra: ' + formatWeight(estimate.extraFillingGrams)
        + (estimate.effect === 'streamers' ? '; streamers upgrade +' + money.format(estimate.effectCents / 100) + ' per cannon' : '');
      return [
        'CUSTOM CANNON ESTIMATE — USD',
        'Size: ' + estimate.size + ' in; iron bottle: ' + formatBottleSize(estimate.bottleCm),
        'Filling: ' + filling,
        'Colors and materials: Choose from paper, metallic, and biodegradable options. We’ll confirm colors and materials after your inquiry.',
        'Quantity: ' + number.format(estimate.quantity) + ' cannons; carton packing to be confirmed',
        'Per cannon: ' + estimate.size + ' in cannon ' + money.format(estimate.cannonCents / 100) + ' + filling ' + money.format(estimate.effectCents / 100) + ' + extra ' + estimate.effect + ' ' + money.format(estimate.extraCents / 100),
        'Estimated unit price: ' + money.format(estimate.unitCents / 100),
        'Estimated product total: ' + money.format(estimate.totalCents / 100),
        'Printing of the customer\'s supplied design is included.',
        'Minimum quantity: ' + MINIMUM_QUANTITY + ' cannons.',
        'Estimate only. We will follow up about design files and confirm specifications, packing and final pricing before production.'
      ].join('\n');
    }
    return Object.freeze({ calculateEstimate, quoteSummary, rates, sizes: SIZES, sizeCm: SIZE_CM, sizeKey,
      extraAmounts: EXTRA_AMOUNTS, quantityPresets: QUANTITY_PRESETS, minimumQuantity: MINIMUM_QUANTITY });
  }

  if (typeof module !== 'undefined' && module.exports) {
    module.exports = { createEstimator, formatWeight, formatBottleSize };
  }

  if (typeof window === 'undefined' || !window.customElements || typeof HTMLElement === 'undefined') return;
  if (window.customElements.get('aaa-custom-estimator')) return;

  class AAACustomEstimator extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.fields = {
        size: Array.from(this.querySelectorAll('[name="contact[size]"]')),
        effect: Array.from(this.querySelectorAll('[name="contact[effect]"]')),
        extraFilling: Array.from(this.querySelectorAll('[name="contact[extra_filling]"]')),
        quantity: Array.from(this.querySelectorAll('[name="contact[quantity]"]'))
      };
      this.quantityField = this.fields.quantity[0];
      this.choiceFields = new Set([...this.fields.size, ...this.fields.effect, ...this.fields.extraFilling]);
      this.quantityPresets = Array.from(this.querySelectorAll('[data-quantity-preset]'));
      this.extraHeadings = Array.from(this.querySelectorAll('[data-extra-heading]'));
      this.extraLabels = Array.from(this.querySelectorAll('[data-extra-label]'));
      this.estimateElements = Array.from(this.querySelectorAll('[data-estimate]'));
      this.summary = this.querySelector('[name="contact[estimate]"]');
      this.note = this.querySelector('[data-estimate-note]');
      this.form = this.querySelector('form');
      this.market = this.dataset.market === 'SE' ? 'SE' : 'US';
      this.money = this.market === 'SE' ? swedishMoney : money;
      this.number = this.market === 'SE' ? swedishNumber : number;
      this.calculator = null;
      try {
        const config = JSON.parse(this.querySelector('script[data-cannon-config]')?.textContent || 'null');
        if (config?.market === this.market) this.calculator = createEstimator(config);
      } catch (_) {
        // update() displays the existing unavailable message and prevents submission.
      }
      this.rates = this.calculator?.rates;
      this.onInput = (event) => {
        if (event.target === this.quantityField) this.update();
      };
      this.onChange = (event) => {
        if (this.choiceFields.has(event.target)) this.update();
      };
      this.onPresetClick = (event) => {
        const button = event.target.closest('[data-quantity-preset]');
        if (!button || !this.quantityPresets.includes(button) || !this.quantityField) return;
        const quantity = integer(button.dataset.quantityPreset);
        if (!this.calculator?.quantityPresets.includes(quantity)) return;
        this.quantityField.value = String(quantity);
        this.quantityField.dispatchEvent(new Event('input', { bubbles: true }));
      };
      this.onReset = () => {
        window.clearTimeout(this.resetTimer);
        this.resetTimer = window.setTimeout(() => this.update(), 0);
      };
      this.onSubmit = (event) => {
        if (!this.update()) {
          event.preventDefault();
          this.form.reportValidity();
        }
      };
      this.addEventListener('input', this.onInput);
      this.addEventListener('change', this.onChange);
      this.addEventListener('click', this.onPresetClick);
      if (this.form) {
        this.form.addEventListener('reset', this.onReset);
        this.form.addEventListener('submit', this.onSubmit);
      }
      this.update();
    }

    disconnectedCallback() {
      this.removeEventListener('input', this.onInput);
      this.removeEventListener('change', this.onChange);
      this.removeEventListener('click', this.onPresetClick);
      if (this.form) {
        this.form.removeEventListener('reset', this.onReset);
        this.form.removeEventListener('submit', this.onSubmit);
      }
      window.clearTimeout(this.resetTimer);
      this.initialized = false;
    }

    update() {
      const input = {};
      Object.entries(this.fields).forEach(([key, fields]) => {
        const field = key === 'quantity' ? fields[0] : fields.find((option) => option.checked);
        input[key] = field ? field.value : null;
      });
      const streamers = input.effect === 'streamers';
      const fillingName = streamers ? 'streamers' : this.market === 'SE' ? 'konfetti' : 'confetti';
      this.extraHeadings.forEach((element) => { element.textContent = (this.market === 'SE' ? 'Lägg till mer ' : 'Add more ') + fillingName; });
      this.extraLabels.forEach((element) => { element.textContent = 'Extra ' + fillingName; });
      this.quantityPresets.forEach((button) => {
        button.setAttribute('aria-pressed', String(integer(input.quantity) === integer(button.dataset.quantityPreset)));
      });
      const estimate = this.calculator ? this.calculator.calculateEstimate(input) : { valid: false, errors: {
        rates: this.market === 'SE' ? 'Priset kan inte beräknas. Kontakta oss för en offert.' : 'An estimate is unavailable. Please request a quote.'
      } };
      Object.entries(this.fields).forEach(([key, fields]) => {
        fields.forEach((field) => { field.setCustomValidity(''); });
        const field = fields.find((option) => option.checked && !option.disabled) || fields.find((option) => !option.disabled);
        if (field && !estimate.valid) field.setCustomValidity(estimate.errors[key] || '');
      });
      if (this.quantityField) this.quantityField.setAttribute('aria-invalid', String(!estimate.valid && Boolean(estimate.errors.quantity)));
      if (this.summary) this.summary.value = this.calculator ? this.calculator.quoteSummary(estimate) : '';
      this.dataset.estimateValid = String(estimate.valid);
      const canonicalSize = this.calculator?.sizeKey(input.size);
      const size = this.calculator?.sizes[canonicalSize];
      const hasFilling = input.effect === 'confetti' || streamers;
      const extra = integer(input.extraFilling);
      const fillingAmount = size && hasFilling && this.calculator.extraAmounts.includes(extra) ? formatWeight(size.confetti + extra, this.market) : '—';
      const weightParts = fillingAmount.split(' (');
      const specifications = {
        cannonLabel: size ? this.market === 'SE' ? this.calculator.sizeCm[canonicalSize] + ' cm rör' : input.size + ' in cannon' : '—',
        bottle: size ? formatBottleSize(size.bottle, this.market) : '—',
        fillingAmount,
        fillingOunces: weightParts[0],
        fillingGrams: weightParts[1] ? '(' + weightParts[1] : '—',
        fillingType: hasFilling ? streamers ? 'Streamers' : this.market === 'SE' ? 'Konfetti' : 'Confetti' : '—'
      };
      if (!estimate.valid) {
        this.estimateElements.forEach((element) => {
          element.textContent = specifications[element.dataset.estimate] || '—';
        });
        if (this.note) this.note.textContent = estimate.errors.quantity || estimate.errors.rates || (this.market === 'SE' ? 'Välj dina alternativ och ange minst ' + this.calculator.minimumQuantity + ' rör för att se priset.' : 'Choose your options and enter at least ' + this.calculator.minimumQuantity + ' cannons to see your estimate.');
        return false;
      }
      const values = {
        unit: this.money.format(estimate.unitCents / 100),
        total: this.money.format(estimate.totalCents / 100),
        cannon: this.money.format(estimate.cannonCents / 100),
        effect: this.money.format(estimate.effectCents / 100),
        extra: this.money.format(estimate.extraCents / 100),
        ...specifications,
        quantity: this.number.format(estimate.quantity)
      };
      this.estimateElements.forEach((element) => {
        if (Object.hasOwn(values, element.dataset.estimate)) element.textContent = values[element.dataset.estimate];
      });
      if (this.note) {
        this.note.textContent = this.market === 'SE' ? 'Minst ' + this.calculator.minimumQuantity + ' rör. Kartongpackningen anpassas efter din order.' : 'Minimum ' + this.calculator.minimumQuantity + ' cannons. Carton packing is flexible.';
      }
      return true;
    }
  }

  window.customElements.define('aaa-custom-estimator', AAACustomEstimator);
})();
